import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageSquare, Plus, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotesSection = ({ notes, functionType, periodType, year, onNoteAdded }) => {
  const { isELT } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      // Get current period dates based on type
      const now = new Date();
      const year_val = year || now.getFullYear();
      let start, end;
      
      if (periodType === 'monthly') {
        const month = now.getMonth() + 1;
        const lastDay = new Date(year_val, month, 0).getDate();
        start = `${year_val}-${String(month).padStart(2, '0')}-01`;
        end = `${year_val}-${String(month).padStart(2, '0')}-${lastDay}`;
      } else if (periodType === 'quarterly') {
        const quarter = Math.ceil((now.getMonth() + 1) / 3);
        const quarters = {
          1: { start: `${year_val}-01-01`, end: `${year_val}-03-31` },
          2: { start: `${year_val}-04-01`, end: `${year_val}-06-30` },
          3: { start: `${year_val}-07-01`, end: `${year_val}-09-30` },
          4: { start: `${year_val}-10-01`, end: `${year_val}-12-31` },
        };
        start = quarters[quarter].start;
        end = quarters[quarter].end;
      } else {
        start = `${year_val}-01-01`;
        end = `${year_val}-12-31`;
      }

      await axios.post(`${API}/notes`, {
        function: functionType,
        period_type: periodType,
        period_start_date: start,
        period_end_date: end,
        year: year_val,
        content: newNote
      });
      
      toast.success('Note added successfully');
      setNewNote('');
      setIsAdding(false);
      onNoteAdded?.();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleUpdateNote = async (noteId) => {
    try {
      await axios.put(`${API}/notes/${noteId}?content=${encodeURIComponent(editContent)}`);
      toast.success('Note updated');
      setEditingId(null);
      onNoteAdded?.();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="notes-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-slate-400" />
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
            Notes & Commentary
          </h3>
        </div>
        {isELT() && !isAdding && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(true)}
            data-testid="add-note-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Note
          </Button>
        )}
      </div>

      {/* Add Note Form */}
      {isAdding && (
        <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="What happened this period? Why did metrics move?"
            className="mb-3 min-h-[100px]"
            data-testid="note-textarea"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAdding(false);
                setNewNote('');
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              data-testid="save-note-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              Save Note
            </Button>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        {(!notes || notes.length === 0) && !isAdding ? (
          <p className="text-sm text-slate-400 text-center py-4">
            No notes for this period
          </p>
        ) : (
          notes?.map((note) => (
            <div key={note.id} className="border-l-2 border-slate-200 pl-4 py-2">
              {editingId === note.id ? (
                <div>
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="mb-2 min-h-[80px]"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleUpdateNote(note.id)}>
                      Save
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {note.content}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-400">
                      {note.created_by_name} • {new Date(note.created_at).toLocaleDateString()}
                    </span>
                    {isELT() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingId(note.id);
                          setEditContent(note.content);
                        }}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotesSection;
